import { useCallback } from 'react';
import { ActivityType } from 'app/data/types/activity';
import { HOST_PLATFORMS } from 'app/shared/consts';
import { useSdkHostInfo } from 'app/shared/contexts/host-info';
import { useBuildingVerticals } from 'app/shared/hooks/use-building-verticals';

/**
 * The types whose vertical the tenant portal renders as its own MFE rather than by framing our
 * miniapp. Work orders and events are absent because the portal has no MFE for them, so naming a
 * button would upgrade nothing.
 *
 * A fourth copy of a map that already exists in helix (`UTILITY_BUTTON_PORTAL_ROUTES`) and the portal
 * (`MFE_PATH_TEMPLATE_BY_VERTICAL`) — deliberately the thinnest one. We only decide WHETHER a button
 * is worth naming; the portal owns the route it turns into, so adding a vertical there does not
 * require a release here, and a stale entry here costs a no-op rather than a wrong url.
 */
const PORTAL_MFE_TYPES = new Set<ActivityType>(['resource_booking', 'visitor_registration']);

/**
 * The utility button to name when asking the host to navigate, or `undefined` to leave the request
 * exactly as it is today.
 *
 * The tenant portal upgrades a webview request that names a button into an in-app route to that
 * vertical's MFE, which is its real desktop experience. Nothing else does: the native apps open the
 * url, and tenant-web has to keep framing the miniapp. So this is gated on the tenant portal
 * SPECIFICALLY — not on `isDesktopHost`, which also matches tenant-web and would change a host that
 * cannot support it.
 *
 * Gated here rather than relying on the portal being the only listener, so tenant-web keeps today's
 * behaviour even where it shares portal code.
 */
export const usePortalMfeButton = (): ((type: ActivityType) => string | undefined) => {
  const { hostPlatform } = useSdkHostInfo();
  const isTenantPortal = hostPlatform === HOST_PLATFORMS.TENANT_PORTAL;
  /*
   * Only the tenant portal can act on the result, so only the tenant portal pays for it. The feed
   * already holds these buttons for its icons, but the booking detail page is its own route and does
   * not — so without this a mobile deeplink straight to a booking fetched a list it would discard.
   */
  const { buttonUuidFor } = useBuildingVerticals({ enabled: isTenantPortal });

  return useCallback(
    (type: ActivityType): string | undefined => {
      if (!isTenantPortal || !PORTAL_MFE_TYPES.has(type)) {
        return undefined;
      }

      return buttonUuidFor(type);
    },
    [isTenantPortal, buttonUuidFor],
  );
};
